import cx from 'classnames'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from 'components/ModalSheet'
import { observer } from 'mobx-react'
import React from 'react'
import {
  CustomSelect,
  RenderSearchItemProps,
  SingleItemSelectedDefault,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import { Form, FormGroupContainer } from 'components/inputs'
import SearchResultItem from 'components/inputs/CustomSelect/SearchResultItem'
import Button from 'components/Button'
import styles from './AddClientAsTeamMember.sass'
import Icon from 'components/Icon'
import ErrorBlock from 'components/ErrorBlock'
import Avatar from 'components/Avatar'
import Member from 'iam/models/Member'
import AddClientsAsTeamMemberViewModel from './AddClientsAsTeamMemberViewModel'

interface AddClientsAsTeamMemberProps {
  vm: AddClientsAsTeamMemberViewModel
}

export default observer(function AddClientsAsTeamMemberDialog({
  vm,
}: AddClientsAsTeamMemberProps) {
  const selectProps = useCustomSelectStateFromItemSource({
    itemSource: vm.fetchTeamMembers,
  })

  const clientError = vm.validation.getError('client')

  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.submit.pending ? undefined : vm.close}
    >
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <div className={styles.contentBlock}>
          <ModalSheetContentTitle align="left" compact>
            <span className={styles.title}>Add a supporting client</span>
          </ModalSheetContentTitle>
          <ModalSheetContentDescription align="left" compact>
            <span className={styles.description}>
              The supporting client will have access to the job
            </span>
          </ModalSheetContentDescription>
        </div>

        <ModalSheetContentBlock>
          <Form className={styles.row}>
            <FormGroupContainer
              field="client"
              model={vm}
              className={styles.selectCollaboratorContainer}
              error={clientError}
            >
              <div className={styles.addCollaboratorLabel}>Client</div>
              <CustomSelect
                {...selectProps}
                multi={false}
                icon={<Icon name="dropdown" className={cx(styles.icon)} />}
                renderItem={(itemProps) => (
                  <SingleItemSelectedDefault
                    {...itemProps}
                    item={itemProps}
                    itemToString={(i) => String(i.item?.displayName)}
                    visual={<Avatar size={24} subject={itemProps.item} />}
                  />
                )}
                placeholder={'Search team members'}
                value={vm.client}
                onChange={vm.setClient}
                renderSearchItem={(props) => (
                  <AddCollaboratorSearchItem {...props} />
                )}
                data-test="add-job-collaborator-as-team-member-dropdown"
              />
            </FormGroupContainer>
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>

      {vm.submit.rejected && (
        <ErrorBlock tackOn="bottom" error={vm.submit.error} />
      )}

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button type="default" onClick={vm.close}>
            Close
          </Button>
          <Button
            type="primary"
            loading={vm.submit.pending}
            data-test="add-job-collaborator-as-team-member-submit-button"
            onClick={vm.submit}
            disabled={vm.disabled}
          >
            Add Client
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

interface AddCollaboratorSearchItem
  extends RenderSearchItemProps<Member | null> {}

const AddCollaboratorSearchItem = React.forwardRef(
  (
    { item, itemToString, ...itemProps }: AddCollaboratorSearchItem,
    ref: React.Ref<any>,
  ) => {
    return (
      <SearchResultItem
        {...itemProps}
        ref={ref}
        visual={<Avatar size={24} subject={item} />}
        data-test={
          'add-job-collaborator-as-team-member-option-' +
          item?.user.publicId?.toLowerCase()
        }
      >
        <>
          <div className={styles.collaboratorName}>{item?.displayName}</div>
          <div className={styles.collaboratorEmail}>{item?.user.email}</div>
        </>
      </SearchResultItem>
    )
  },
)
